/**
 * App with Identity Layer
 * Wraps the main app with multi-identity management
 * Handles authentication, switching, and per-identity store initialization
 */

import React, { useState, useEffect, useMemo } from 'react';
import { IdentityProvider, useIdentity } from './use-identity';
import { PreferencesProvider } from './use-global-preferences';
import { PoeticLogin } from './poetic-login';
import { IdentitySwitcher } from './identity-switcher';
import { IdentityManager } from './identity-manager';
import { SpatialCanvas } from './spatial-canvas';
import { SpatialCanvasWithFloor } from './spatial-canvas-with-floor';
import { CommunityWelcome } from './community-welcome';
import { CommunitiesPanel } from './communities-panel';
import { CommunityMembersView } from './community-members-view';
import { useNetworkCommunities } from './use-network-communities';
import { useCommunityPresence } from './use-community-presence';
import { useKeyboardTyping } from './use-typing-indicator';
import type { PoeticIdentity } from '@maco144/shared';
import { formatIdentity } from '@maco144/shared';
import type { UserId, RegionId } from '@maco144/shared';
import type { CommunityMember } from './community-presence-client';

/**
 * Inner component that uses the identity hook
 * This must be inside the IdentityProvider
 */
function AppWithIdentityInner() {
  const {
    isAuthenticated,
    isLoading,
    identities,
    createIdentity,
    switchIdentity,
    error,
    currentIdentity,
  } = useIdentity();

  const [showIdentityManager, setShowIdentityManager] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showCommunityWelcome, setShowCommunityWelcome] = useState(false);
  const [showCommunitiesPanel, setShowCommunitiesPanel] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState<{
    regionId: RegionId;
    communityName: string;
    emoji: string;
  } | null>(null);
  const [lastAuthenticatedIdentityId, setLastAuthenticatedIdentityId] = useState<UserId | null>(
    null
  );
  const [presenceData, setPresenceData] = useState<Record<RegionId, CommunityMember[]>>({});

  // DISABLED: These hooks fire too frequently and freeze the UI
  // TODO: Fix the infinite re-render loop in presence/typing hooks
  const networkCommunities = { communities: null, connected: false, error: null, loading: false };
  const avatarObject = null;
  const roleChannelPresence = { members: [], updateStatus: () => {} };
  const roleForumPresence = { members: [], updateStatus: () => {} };
  const roleChannelTyping = { handleKeyDown: () => {} };
  const roleForumTyping = { handleKeyDown: () => {} };

  // DISABLED: Global keyboard listener causes performance issues
  // useEffect(() => {
  //   if (!isAuthenticated) return;
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     roleChannelTyping.handleKeyDown();
  //     roleForumTyping.handleKeyDown();
  //   };
  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [isAuthenticated, roleChannelTyping, roleForumTyping]);

  // Show welcome modal on first authentication of this identity
  useEffect(() => {
    if (
      networkCommunities.communities &&
      currentIdentity &&
      currentIdentity.userId !== lastAuthenticatedIdentityId
    ) {
      // Check if we've already shown the welcome for this identity
      const alreadyShown = localStorage.getItem(
        `communities-welcome-shown-${currentIdentity.userId}`
      );

      if (!alreadyShown) {
        setShowCommunityWelcome(true);
      }

      setLastAuthenticatedIdentityId(currentIdentity.userId);

      // Save flag to localStorage to not show again next time
      localStorage.setItem(`communities-welcome-shown-${currentIdentity.userId}`, 'true');
    }
  }, [networkCommunities.communities, currentIdentity, lastAuthenticatedIdentityId]);

  // Show loading screen only during initial startup (not during mid-session operations like switchIdentity)
  if (isLoading && !isAuthenticated) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f1419 0%, #1a2332 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <div style={{ textAlign: 'center', color: '#e1e8ed' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌅</div>
          <div style={{ fontSize: '20px', fontWeight: '600' }}>Loading identities...</div>
        </div>
      </div>
    );
  }

  // Not authenticated - show login/selection
  if (!isAuthenticated) {
    return (
      <IdentityLoginScreen
        identities={identities}
        onCreateIdentity={createIdentity}
        onSwitchIdentity={switchIdentity}
        onPasswordChange={setPasswordInput}
        passwordInput={passwordInput}
        isCreating={isCreating}
        onCreatingChange={setIsCreating}
        error={error?.message || null}
      />
    );
  }

  // Authenticated - show app with identity UI
  const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/`;

  return (
    <PreferencesProvider userId={currentIdentity!.userId} wsUrl={wsUrl}>
      <SpatialCanvasWithFloor userId={currentIdentity!.userId} />
      <IdentityManager isOpen={showIdentityManager} onClose={() => setShowIdentityManager(false)} />
    </PreferencesProvider>
  );
}

/**
 * Login screen with identity selection and creation
 */
function IdentityLoginScreen({
  identities,
  onCreateIdentity,
  onSwitchIdentity,
  onPasswordChange,
  passwordInput,
  isCreating,
  onCreatingChange,
  error,
}: {
  identities: any[];
  onCreateIdentity: (phrase: PoeticIdentity, name: string, password: string) => Promise<void>;
  onSwitchIdentity: (userId: string, password: string) => Promise<void>;
  onPasswordChange: (password: string) => void;
  passwordInput: string;
  isCreating: boolean;
  onCreatingChange: (creating: boolean) => void;
  error: string | null;
}): JSX.Element {
  const [tempName, setTempName] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [tempConfirmPassword, setTempConfirmPassword] = useState('');

  const handleClearAllData = async () => {
    if (!confirm('This will delete ALL identities and reset the app. Are you sure?')) {
      return;
    }
    try {
      // Clear IndexedDB databases
      const dbs = await indexedDB.databases();
      for (const db of dbs) {
        if (db.name?.includes('rising-sun')) {
          indexedDB.deleteDatabase(db.name);
        }
      }
      // Reload page to reinitialize
      window.location.reload();
    } catch (err) {
      alert('Failed to clear data: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handlePoeticLogin = async (identity: PoeticIdentity) => {
    if (!tempName.trim()) {
      alert('Please enter an identity name (shareable)');
      return;
    }

    if (!tempPassword.trim()) {
      alert('Please enter a password');
      return;
    }

    if (tempPassword !== tempConfirmPassword) {
      alert('Passwords do not match');
      return;
    }

    try {
      await onCreateIdentity(identity, tempName, tempPassword);
      setTempName('');
      setTempPassword('');
      setTempConfirmPassword('');
      onCreatingChange(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create identity');
    }
  };

  const handleSelectIdentity = async (userId: string) => {
    if (!passwordInput.trim()) {
      alert('Please enter the password for this identity');
      return;
    }

    try {
      await onSwitchIdentity(userId, passwordInput);
      onPasswordChange('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to switch identity');
    }
  };

  if (isCreating) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f1419 0%, #1a2332 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: '500px',
            width: '100%',
            padding: '40px',
            borderRadius: '20px',
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 20px 60px rgba(31, 38, 135, 0.25)',
          }}
        >
          <h2 style={{ color: '#e1e8ed', marginBottom: '8px', fontSize: '20px' }}>
            Create New Identity
          </h2>
          <p style={{ color: '#9ca3af', margin: '0 0 20px 0', fontSize: '13px' }}>
            NAME: Shareable identifier • PASSWORD: Backend authentication
          </p>

          {/* Identity Name */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                color: '#9ca3af',
                fontSize: '12px',
                marginBottom: '6px',
              }}
            >
              Identity Name (Shareable)
            </label>
            <input
              type="text"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              placeholder="e.g., Alice, luminous-phoenix, etc."
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(0, 0, 0, 0.2)',
                color: '#e1e8ed',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                color: '#9ca3af',
                fontSize: '12px',
                marginBottom: '6px',
              }}
            >
              Password
            </label>
            <input
              type="password"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.target.value)}
              placeholder="Password"
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(0, 0, 0, 0.2)',
                color: '#e1e8ed',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                color: '#9ca3af',
                fontSize: '12px',
                marginBottom: '6px',
              }}
            >
              Confirm Password
            </label>
            <input
              type="password"
              value={tempConfirmPassword}
              onChange={(e) => setTempConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(0, 0, 0, 0.2)',
                color: '#e1e8ed',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Back Button */}
          <button
            onClick={() => onCreatingChange(false)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#e1e8ed',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '8px',
            }}
          >
            Back
          </button>

          {/* Reset Button */}
          <button
            onClick={handleClearAllData}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              background: 'transparent',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: '12px',
              marginBottom: '16px',
            }}
          >
            Clear All Data (Reset)
          </button>

          {/* Poetic login */}
          <PoeticLogin onAuth={(userId, identity) => handlePoeticLogin(identity)} loading={false} />
        </div>
      </div>
    );
  }

  // Selection screen
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f1419 0%, #1a2332 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: '500px',
          width: '100%',
          padding: '40px',
          borderRadius: '20px',
          background: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 20px 60px rgba(31, 38, 135, 0.25)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🌅</div>
          <h1
            style={{ fontSize: '32px', fontWeight: '700', color: '#e1e8ed', margin: '0 0 8px 0' }}
          >
            Rising Sun
          </h1>
          <p style={{ fontSize: '14px', color: '#9ca3af', margin: '0' }}>
            {identities.length === 0 ? 'Create your first identity' : 'Select an identity'}
          </p>
        </div>

        {error && (
          <div
            style={{
              padding: '12px',
              borderRadius: '8px',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              fontSize: '12px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {isCreating ? (
          <div>
            <h2 style={{ color: '#e1e8ed', marginBottom: '8px', fontSize: '20px' }}>
              Create New Identity
            </h2>
            <p style={{ color: '#9ca3af', margin: '0 0 20px 0', fontSize: '13px' }}>
              NAME: Shareable identifier • PASSWORD: Backend authentication
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  color: '#9ca3af',
                  fontSize: '12px',
                  marginBottom: '6px',
                }}
              >
                Identity Name (Shareable)
              </label>
              <input
                type="text"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                placeholder="e.g., Alice, luminous-phoenix, etc."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.2)',
                  color: '#e1e8ed',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  color: '#9ca3af',
                  fontSize: '12px',
                  marginBottom: '6px',
                }}
              >
                Password (Backend Authentication)
              </label>
              <input
                type="password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Password"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.2)',
                  color: '#e1e8ed',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  color: '#9ca3af',
                  fontSize: '12px',
                  marginBottom: '6px',
                }}
              >
                Confirm Password
              </label>
              <input
                type="password"
                value={tempConfirmPassword}
                onChange={(e) => setTempConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.2)',
                  color: '#e1e8ed',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <button
              onClick={() => onCreatingChange(false)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#e1e8ed',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '16px',
              }}
            >
              Back
            </button>
          </div>
        ) : identities.length === 0 ? (
          <div>
            <p style={{ color: '#9ca3af', textAlign: 'center', marginBottom: '20px' }}>
              Create your first poetic identity
            </p>
            <button
              onClick={() => onCreatingChange(true)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: '#667eea',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              Create Identity
            </button>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  color: '#9ca3af',
                  fontSize: '12px',
                  marginBottom: '8px',
                }}
              >
                SELECT IDENTITY
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {identities.map((identity) => (
                  <button
                    key={identity.userId}
                    onClick={() => {
                      // Identity selected, now need password
                    }}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#e1e8ed',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>
                      {identity.displayName || formatIdentity(identity.phrase)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                      {formatIdentity(identity.phrase)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  color: '#9ca3af',
                  fontSize: '12px',
                  marginBottom: '8px',
                }}
              >
                PASSWORD
              </label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => onPasswordChange(e.target.value)}
                placeholder="Enter password"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && identities.length > 0) {
                    handleSelectIdentity(identities[0].userId);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.2)',
                  color: '#e1e8ed',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <button
              onClick={() => handleSelectIdentity(identities[0].userId)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: '#667eea',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px',
              }}
            >
              Login
            </button>

            <button
              onClick={() => onCreatingChange(true)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'transparent',
                color: '#e1e8ed',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '8px',
              }}
            >
              Create New Identity
            </button>

            <button
              onClick={handleClearAllData}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                background: 'transparent',
                color: '#ef4444',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Clear All Data (Reset)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Root wrapper with IdentityProvider
 */
export function AppWithIdentity(): JSX.Element {
  return (
    <IdentityProvider>
      <AppWithIdentityInner />
    </IdentityProvider>
  );
}
